import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { splitLanguageFromPath } from '../../lib/localeRoutes';

// Scrolls to the section matching the current route path after navigation.
export function useScrollToSection() {
  const location = useLocation();

  useEffect(() => {
    // Strip the language prefix first: on /de/rooms the section is still "rooms".
    const { path } = splitLanguageFromPath(location.pathname);
    const sectionId = path.replace(/\/$/, '').slice(1) || 'home';

    if (sectionId === 'home') {
      window.scrollTo(0, 0);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Give up after ~5s rather than retrying forever on a path that has no
    // matching section (e.g. a route that falls through to the homepage).
    let attemptsLeft = 50;
    let userScrolled = false;
    let realign: (() => void) | undefined;
    const onUserScroll = () => {
      userScrolled = true;
    };
    const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown'] as const;
    USER_SCROLL_EVENTS.forEach((e) =>
      window.addEventListener(e, onUserScroll, { passive: true, once: true }),
    );

    // Retries until the target section is mounted, then performs smooth scroll.
    const scrollToElement = () => {
      if (cancelled) return;
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Sections below the fold use content-visibility placeholder heights
        // (index.css), so the smooth scroll can land a bit off once they
        // render for real. Re-align when it ends — unless the visitor has
        // started scrolling themselves.
        let correctionsLeft = 2;
        realign = () => {
          if (cancelled || userScrolled || correctionsLeft-- <= 0) return;
          if (Math.abs(element.getBoundingClientRect().top) > 4) {
            element.scrollIntoView({ behavior: 'auto', block: 'start' });
            timer = setTimeout(realign!, 300);
          }
        };
        if ('onscrollend' in window) {
          window.addEventListener('scrollend', realign, { once: true });
        } else {
          timer = setTimeout(realign, 1200);
        }
      } else if (attemptsLeft-- > 0) {
        timer = setTimeout(scrollToElement, 100);
      }
    };

    const start = () => {
      timer = setTimeout(scrollToElement, 100);
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('load', start);
      if (realign) window.removeEventListener('scrollend', realign);
      USER_SCROLL_EVENTS.forEach((e) => window.removeEventListener(e, onUserScroll));
    };
  }, [location.pathname]);
}
